/**
 * Signing in, as the page sees it. Story VC-017.
 *
 * Three states, and the page renders one of them:
 *
 * ```text
 * "static"     no identity provider is configured, so the page runs on the
 *              token it has always used. The development path
 * "signing-in" a redirect is in flight, or a code is being exchanged
 * "signed-in"  there is an access token
 * ```
 *
 * A fourth would be "signed out", and there is not one: signing out
 * navigates away, and a page that stayed to show a signed-out state would
 * be showing it after the tab's state no longer means anything.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EXPIRY_SKEW_SECONDS } from "./oidc.js";
import { beginSignIn, completeSignIn, configFrom, freshToken, signOut } from "./session.js";

export function useAuth(env, staticToken) {
  const config = useMemo(() => configFrom(env), [env]);
  const [tokens, setTokens] = useState(null);
  const [problem, setProblem] = useState(null);
  // A ref as well as state: `token()` below is handed to callers that keep
  // it, and a closure over state would hand them an expired token after a
  // refresh.
  const held = useRef(null);

  const replace = useCallback((next) => {
    held.current = next;
    setTokens(next);
  }, []);

  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    completeSignIn(config)
      .then((signedIn) => {
        if (cancelled) return;
        if (signedIn) {
          replace(signedIn);
        } else {
          // Not a redirect back, and nothing held: go and get one. The
          // provider answers without a prompt when its own session is
          // live, so a page refresh is a flicker rather than a login.
          beginSignIn(config).catch((error) => setProblem(String(error.message)));
        }
      })
      .catch((error) => {
        if (!cancelled) setProblem(String(error.message));
      });
    return () => {
      cancelled = true;
    };
  }, [config, replace]);

  // Refreshed *before* it is needed, on a timer, rather than when a call
  // fails. A live voice session holds one socket for the length of a
  // conversation; waiting for a 401 would mean the conversation is what
  // discovers the expiry.
  useEffect(() => {
    if (!config || !tokens) return undefined;
    const seconds = Math.max(
      0,
      tokens.expiresAt - EXPIRY_SKEW_SECONDS - Math.floor(Date.now() / 1000),
    );
    const timer = setTimeout(() => {
      freshToken(config, tokens, replace).catch((error) =>
        // A failed refresh ends the session visibly. Degrading quietly
        // would leave a page whose every call 401s and no reason on screen.
        setProblem(`the session could not be renewed: ${error.message}`),
      );
    }, seconds * 1000);
    return () => clearTimeout(timer);
  }, [config, tokens, replace]);

  const token = useCallback(async () => {
    if (!config) return staticToken;
    if (!held.current) throw new Error("not signed in");
    return freshToken(config, held.current, replace);
  }, [config, replace, staticToken]);

  return {
    state: !config ? "static" : tokens ? "signed-in" : "signing-in",
    problem,
    /// The value to attach to a call now. Changes when a refresh lands,
    /// which is what re-renders the screens holding it.
    accessToken: tokens ? tokens.accessToken : staticToken,
    token,
    signOut: useCallback(() => config && signOut(config), [config]),
  };
}
